import { useModal } from "hooks/useModal"

import { memo, useCallback, useState } from "react"
import styled from "styled-components"

import Heading from "components/Typography/Heading"
import BodyText from "components/Typography/BodyText"

import ModalV2 from "components/EvtKit/modal/ModalV2"
import { IColor } from "react-color-palette"
import Button from "components/EvtKit/button/Button"
import clsx from "clsx"
import BannerCard from "./BannerCard/BannerCard"
import BannerColorFilter from "./BannerColorFilter"
import BannerImageInfo from "./BannerImageInfo"
import Toast from "components/EvtKit/Toast/ToastComponent"
import FilesUpload from "components/EvtKit/file-upload/FilesUpload"
import FileUploadButtonV2 from "components/EvtKit/file-upload/FileUploadButtonV2"
import { useAppDispatch } from "reducers/store"
import { showToast } from "reducers/toast/toast-actions"
import { invalidFileFormat } from "pages/products/constants/defaultValues"
import EmailCard from "./EmailCard/EmailCard"
import Tabs from "components/EvtKit/tabs/Tabs"
import TabsContent from "components/EvtKit/tabs/TabsContent"
import TabsListGradient from "components/EvtKit/tabs/tabsGradient/TabsListGradient"
import TabsTriggerGradient from "components/EvtKit/tabs/tabsGradient/TabsTriggerGradient"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 28px 0px 24px;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
`

const BannerInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 24px;
  gap: 40px;
`

const BannerCustomizeContainer = styled.div`
  width: 100%;
`

const SeparatorLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.palette.mediumDarkGray};
  opacity: 0.3;
  margin-top: 8px;
`

const ButtonsContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  margin-top: 28px;
  width: 100%;
  gap: 24px;
`

const ButtonCustom = styled(FileUploadButtonV2)`
  width: 200px;
`

const ToastContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 32px;
  white-space: nowrap;
`

const TabsListContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  height: 40px;

  .tabs-trigger {
    width: 347px;
  }
`

const TabsContainer = styled.div`
  margin-top: 24px;
`

const StyledTabContent = styled(TabsContent)`
  max-width: 226px;
  width: 100%;
`

const tabIds = {
  banner: "banner",
  emailTemplate: "emailTemplate"
}

interface Props {
  logoFile: File | undefined
  color: IColor
  setColor: React.Dispatch<React.SetStateAction<IColor>>
  colorSecondary: IColor
  setColorSecondary: React.Dispatch<React.SetStateAction<IColor>>
  logoUrl: string
  bannerImageUrl: string
  bannerInfoForm: App.BannerInfoForm
  isValidBannerInfoForm: boolean
  handleBannerInfoFormChange: <K extends keyof App.BannerInfoForm>(key: K, value: App.BannerInfoForm[K]) => void
  handleBannerInfoFormConfirm: () => void
  handleBannerInfoFormCancel: () => void
}

const ProductBannerModal = (props: Props) => {
  const {
    logoFile,
    logoUrl,
    bannerImageUrl,
    bannerInfoForm,
    color,
    setColor,
    colorSecondary,
    setColorSecondary,
    isValidBannerInfoForm,
    handleBannerInfoFormChange,
    handleBannerInfoFormCancel,
    handleBannerInfoFormConfirm
  } = props

  const { isOpen, type, closeModal } = useModal()

  const appDispatch = useAppDispatch()

  const [uploadError, setUploadError] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  const handleToastClose = useCallback(() => {
    setIsClosing(true)
  }, [])

  const handleToastOpen = useCallback(() => {
    setIsClosing(false)
    setUploadError(true)
  }, [])

  const handleCleanModalState = useCallback(() => {
    handleBannerInfoFormCancel()
    closeModal()
  }, [handleBannerInfoFormCancel, closeModal])

  const handleBannerFileChange = useCallback(
    (files: File[]) => {
      const file = files[0]

      if (!file) {
        appDispatch(showToast(invalidFileFormat, "error"))

        return
      }

      const fileSizeInMB = file.size / (1024 * 1024)

      if (fileSizeInMB > 2) {
        return handleToastOpen()
      }
      handleBannerInfoFormChange("imageFile", file)
      if (uploadError) handleToastClose()
    },
    [uploadError]
  )

  const onConfirm = useCallback(() => {
    handleBannerInfoFormConfirm()
    closeModal()
  }, [handleBannerInfoFormConfirm])

  return (
    <>
      <ModalV2 openModal={isOpen && type === "addProperty"} closeModal={handleCleanModalState} size="1-lg">
        <Container>
          <Heading variant="h4" color="gray-dark">
            Identidade visual
          </Heading>
          <SeparatorLine />
          <ContentContainer>
            <BodyText>
              Defina a identidade visual do seu produto, lembrando que uma imagem coerente pode aumentar o engajamento e
              destaque.
            </BodyText>
          </ContentContainer>
          <TabsContainer>
            <Tabs defaultActiveTabId={tabIds.banner}>
              <TabsListContainer>
                <TabsListGradient className="fill-width">
                  <TabsTriggerGradient label="Banner do produto" triggerId={tabIds.banner} bgClassName="tabs-trigger" />
                  <TabsTriggerGradient
                    label="Template de e-mail"
                    triggerId={tabIds.emailTemplate}
                    bgClassName="tabs-trigger"
                  />
                </TabsListGradient>
              </TabsListContainer>
              <BannerInfoContainer>
                <StyledTabContent triggerId={tabIds.banner}>
                  <BannerCard
                    logoFile={logoFile}
                    backgroundColor={color.hex}
                    backgroundColorSecondary={colorSecondary.hex}
                    bannerImageFile={bannerInfoForm.imageFile}
                    logoUrl={logoUrl}
                    bannerImageUrl={bannerImageUrl}
                    handleBannerFileChange={handleBannerFileChange}
                    isGradient={bannerInfoForm.isGradient}
                  />
                </StyledTabContent>
                <StyledTabContent triggerId={tabIds.emailTemplate}>
                  <EmailCard
                    color={color.hex}
                    colorSecondary={colorSecondary.hex}
                    isGradient={bannerInfoForm.isGradient}
                    logoFile={logoFile}
                    logoUrl={logoUrl}
                  />
                </StyledTabContent>
                <BannerColorFilter
                  color={color}
                  setColor={setColor}
                  colorSecondary={colorSecondary}
                  setColorSecondary={setColorSecondary}
                  bannerInfoForm={bannerInfoForm}
                  handleBannerInfoFormChange={handleBannerInfoFormChange}
                />
              </BannerInfoContainer>
            </Tabs>
          </TabsContainer>
          <BannerCustomizeContainer>
            <BannerImageInfo handleBannerFileChange={handleBannerFileChange} />
          </BannerCustomizeContainer>
          <ButtonsContainer>
            <Button onClick={handleCleanModalState} label="Cancelar" className={clsx("secondary fill-width")} />
            <Button
              type="button"
              onClick={onConfirm}
              label="Confirmar"
              className={clsx("background-gradient fill-width")}
              disabled={!isValidBannerInfoForm}
            />
          </ButtonsContainer>
        </Container>
      </ModalV2>
      {uploadError && (
        <Toast
          persistent
          closeOnClickOutside={!isClosing}
          onClose={handleToastClose}
          variant="error"
          isClosing={isClosing}
        >
          <ToastContainer>
            <BodyText size="medium" color="error">
              Tamanho de imagem excedido.
            </BodyText>
            <FilesUpload acceptFormats={["image/png", "image/webp"]} onFilesChange={handleBannerFileChange}>
              <ButtonCustom label={"Escolher outra imagem"} className="secondary-error" />
            </FilesUpload>
          </ToastContainer>
        </Toast>
      )}
    </>
  )
}

export default memo(ProductBannerModal)
